import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, AlertTriangle, Globe } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { ApplicationTypeSelect } from '../atoms/ApplicationTypeSelect';
import { AppLanguagesTransferList } from '../organisms/AppLanguagesTransferList';
import { useApplicationLanguagesStore } from '../../stores/application-languages-store';
import { useTranslationStore } from '../../stores/translation-store';
import { ApplicationType } from '../../types/translation-types';
import { toast } from 'sonner';

export function AppLanguagesConfig() {
  const navigate = useNavigate();

  // Use shared app type from translation store
  const { selectedAppType, setSelectedAppType } = useTranslationStore();

  const {
    applicationLanguages,
    applicationName,
    availableLanguages,
    pendingLanguageIds,
    isLoading,
    isLoadingAvailable,
    isSaving,
    error,
    saveError,
    hasUnsavedChanges,
    setApplicationType,
    addLanguage,
    removeLanguage,
    reorderLanguages,
    saveChanges,
    discardChanges,
  } = useApplicationLanguagesStore();

  // Load data when app type changes
  useEffect(() => {
    if (selectedAppType !== null) {
      setApplicationType(selectedAppType);
    }
  }, [selectedAppType, setApplicationType]);

  const handleAppTypeChange = useCallback((appType: ApplicationType | null) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to switch applications?'
      );
      if (!confirmed) return;
    }
    setSelectedAppType(appType);
  }, [hasUnsavedChanges, setSelectedAppType]);

  const handleSave = useCallback(async () => {
    try {
      await saveChanges();
      toast.success('Application languages saved successfully');
    } catch {
      toast.error('Failed to save application languages');
    }
  }, [saveChanges]);

  const handleDiscard = useCallback(() => {
    discardChanges();
    toast.info('Changes discarded');
  }, [discardChanges]);

  const isLoadingAny = isLoading || isLoadingAvailable;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header - fixed height */}
      <div className="flex-shrink-0 px-6 py-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (hasUnsavedChanges) {
                  const confirmed = window.confirm(
                    'You have unsaved changes. Are you sure you want to leave?'
                  );
                  if (!confirmed) return;
                }
                navigate('/translations');
              }}
              className="h-8 w-8 p-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Language Settings</h1>
              <p className="text-muted-foreground">
                Configure which languages each application supports
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/translations/global-languages')}>
              <Globe className="h-4 w-4 mr-2" />
              Manage Global Languages
            </Button>
            {hasUnsavedChanges && (
              <Button variant="outline" onClick={handleDiscard} disabled={isSaving}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Discard
              </Button>
            )}
            <Button onClick={handleSave} disabled={!hasUnsavedChanges || isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Application Selector */}
        <div className="flex items-center gap-4">
          <ApplicationTypeSelect
            value={selectedAppType}
            onChange={handleAppTypeChange}
            className="w-[300px]"
            required
          />
          {applicationName && (
            <span className="text-sm text-gray-500">
              Configuring: <strong>{applicationName}</strong>
            </span>
          )}
          {hasUnsavedChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              Unsaved changes
            </span>
          )}
        </div>

        {/* Info Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
          <strong>Note:</strong> If no languages are configured for an application, all active
          languages will be used by default. Configure specific languages to limit which
          translations are available in the application.
        </div>

        {/* Error Display */}
        {(error || saveError) && (
          <Alert variant="destructive">
            <AlertDescription>{error || saveError}</AlertDescription>
          </Alert>
        )}
      </div>

      {/* Transfer List - flex-1 to fill remaining space */}
      <div className="flex-1 min-h-0 overflow-auto px-6 pb-6">
        <AppLanguagesTransferList
          availableLanguages={availableLanguages}
          assignedLanguageIds={pendingLanguageIds}
          applicationLanguages={applicationLanguages}
          onAdd={addLanguage}
          onRemove={removeLanguage}
          onReorder={reorderLanguages}
          isLoading={isLoadingAny}
        />
      </div>
    </div>
  );
}
